import { TC_GECERSIZLIK_METNI, tcKimlikDogrula } from "@koclukpanel/contracts";

/**
 * ÖĞRENCİ KAYIT FORMU — saf kurallar (F-PPL-01, F-PPL-05b)
 *
 * ================================================================
 * BURASI SUNUCUNUN YERİNE GEÇMEZ
 * ================================================================
 * Kararı sunucu veriyor; buradaki kontroller yalnızca kullanıcıya
 * ERKEN söylüyor. `kisiler.controller.ts` aynı kuralları Zod ile
 * tekrar uyguluyor ve son söz onun.
 *
 * Ayrı bir dosya olmasının sebebi: veritabanı, sunucu ve tarayıcı
 * olmadan test edilebilsin. Yaş hesabı bir tarihe bağlı ve tarihe
 * bağlı kod, elle denenince "bugün çalışıyor" der.
 */

/** `GradeLevel` enum'unun tamamı — sunucudaki `SINIFLAR` ile aynı sıra. */
export const SINIFLAR = [
  "G5",
  "G6",
  "G7",
  "G8",
  "G9",
  "G10",
  "G11",
  "G12",
  "GRADUATE",
  "OTHER",
] as const;

export type Sinif = (typeof SINIFLAR)[number];

export interface KayitFormu {
  ad: string;
  soyad: string;
  eposta: string;
  telefon: string;
  dogumTarihi: string;
  sinif: Sinif | "";
  /**
   * F-PPL-05c — öğrencinin alanı (Müfredat'taki alan kimliği). İsteğe
   * bağlı; boşsa program ekranı ders listesini getiremez ve bunu söyler.
   */
  alan: string;
  davetGonder: boolean;
  /** G-AUT-00 — T.C. ile giriş için. İsteğe bağlı. */
  tcKimlik: string;
  /** C39 — ortak numarada bu kişi mi telefonuyla giriyor. */
  telefonSahibi: boolean;
  /*
    ================================================================
    HEDEF ALANLARI — HEPSİ İSTEĞE BAĞLI (F-PPL-05)
    ================================================================
    Emre'nin kararı (05.09.2026): hedef hem kayıtta girilebilsin hem
    koç sonradan değiştirebilsin, çünkü hedef yıl içinde değişiyor.

    Kayıtta ZORUNLU değiller ve olmamalılar: kaydı açan kişi çoğu
    zaman ilk gün hedefi bilmiyor. Zorunlu olsalardı ya kayıt
    engellenirdi ya da uydurma bir değer girilirdi — ikisi de
    "boş" bırakmaktan kötü.
  */
  hedefSinav: string;
  hedefPuan: string;
  hedefSiralama: string;
  hedefOkul: string;
  sinavTarihi: string;
}

/** F-CRM-08b — adayın yalnızca öğrenciye ait iletişimi taşınır; eksik alanlar elle tamamlanır. */
export function adayBilgileriniTasi(
  form: KayitFormu,
  aday: { studentName: string; studentEmail: string | null; studentPhone: string | null },
): KayitFormu {
  const parcalar = aday.studentName.trim().split(/\s+/);
  const soyad = parcalar.length > 1 ? parcalar.pop()! : "";
  return {
    ...form,
    ad: parcalar.join(" "),
    soyad,
    eposta: aday.studentEmail ?? "",
    telefon: aday.studentPhone ?? "",
    telefonSahibi: false,
  };
}

export const BOS_FORM: KayitFormu = {
  ad: "",
  soyad: "",
  eposta: "",
  telefon: "",
  dogumTarihi: "",
  sinif: "",
  alan: "",
  davetGonder: true,
  tcKimlik: "",
  telefonSahibi: false,
  hedefSinav: "",
  hedefPuan: "",
  hedefSiralama: "",
  hedefOkul: "",
  sinavTarihi: "",
};

/** Alan adı → hata metni. Boş nesne = form gönderilebilir. */
export type Hatalar = Partial<Record<keyof KayitFormu, string>>;

/**
 * Doğum tarihine göre TAM yaş.
 *
 * `bugun` parametre olarak veriliyor: içeride `new Date()` çağırsaydı
 * test "doğum gününde 18 oluyor mu" sorusunu soramazdı — ki 18 yaş
 * sınırının etrafındaki tek gün, kuralın tamamının döndüğü gün.
 */
export function yasHesapla(dogumTarihi: string, bugun: Date): number | null {
  const e = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dogumTarihi);
  if (!e) return null;
  const [, yil, ay, gun] = e.map(Number) as [unknown, number, number, number];

  // Geçersiz takvim günü (31 Şubat gibi) sessizce kaymasın.
  const d = new Date(Date.UTC(yil, ay - 1, gun));
  if (d.getUTCFullYear() !== yil || d.getUTCMonth() !== ay - 1 || d.getUTCDate() !== gun) {
    return null;
  }

  let yas = bugun.getUTCFullYear() - yil;
  const dogumGunuGeldiMi =
    bugun.getUTCMonth() > ay - 1 || (bugun.getUTCMonth() === ay - 1 && bugun.getUTCDate() >= gun);
  if (!dogumGunuGeldiMi) yas -= 1;
  return yas;
}

/**
 * F-PPL-05b — 18 yaş altı hesap, veli muvafakati kaydedilmeden
 * aktifleşmez.
 *
 * Form bunu ENGEL olarak değil UYARI olarak gösteriyor: kayıt açılır,
 * davet gider, öğrenci şifresini belirler — yalnızca giriş yapamaz.
 * Engel olsaydı 18 yaş altı öğrenci hiç kaydedilemezdi ve bu firmanın
 * öğrencilerinin çoğu 18 yaş altı.
 */
export function muvafakatGerekiyorMu(dogumTarihi: string, bugun: Date): boolean {
  const yas = yasHesapla(dogumTarihi, bugun);
  return yas !== null && yas < 18;
}

const EPOSTA = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function dogrula(f: KayitFormu, bugun: Date): Hatalar {
  const h: Hatalar = {};

  if (f.ad.trim().length === 0) h.ad = "Ad boş olamaz.";
  if (f.soyad.trim().length === 0) h.soyad = "Soyad boş olamaz.";

  if (f.eposta.trim().length === 0) h.eposta = "E-posta boş olamaz.";
  else if (!EPOSTA.test(f.eposta.trim())) h.eposta = "E-posta geçerli görünmüyor.";

  if (f.sinif === "") h.sinif = "Sınıf seçin.";

  if (f.dogumTarihi.trim().length === 0) {
    // F-PPL-05b: zorunlu. İsteğe bağlı olsaydı 18 yaş koruması hiç
    // devreye giremezdi.
    h.dogumTarihi = "Doğum tarihi zorunlu — 18 yaş kuralı buna bağlı.";
  } else {
    const yas = yasHesapla(f.dogumTarihi, bugun);
    if (yas === null) h.dogumTarihi = "Doğum tarihi geçerli bir gün değil.";
    else if (yas < 0) h.dogumTarihi = "Doğum tarihi gelecekte olamaz.";
    else if (yas > 100) h.dogumTarihi = "Doğum tarihi çok eski görünüyor.";
  }

  /*
    T.C. — İSTEĞE BAĞLI ama yazılırsa geçerli olmalı. Mesaj nedene
    özel: "geçersiz" demek, on bir hane yazan kişiye numarayı
    DÜZELTTİRMEZ.

    Sunucu aynı kontrolü tekrar yapıyor ve son söz onun; burası
    yalnızca erken söylüyor.
  */
  const tc = f.tcKimlik.trim();
  if (tc.length > 0) {
    const sonuc = tcKimlikDogrula(tc);
    if (!sonuc.gecerli) h.tcKimlik = TC_GECERSIZLIK_METNI[sonuc.neden];
  }

  /*
    C39 — telefonu olmayan biri numaranın sahibi olamaz. Asıl kural
    veritabanında (CHECK kısıtı); burası yalnızca formu göndermeden
    önce söylüyor.
  */
  if (f.telefonSahibi && f.telefon.trim().length === 0) {
    h.telefonSahibi = "Sahip işareti için önce telefon girin.";
  }

  return h;
}

export function gonderilebilir(f: KayitFormu, bugun: Date): boolean {
  return Object.keys(dogrula(f, bugun)).length === 0;
}

/** Sunucunun beklediği gövde. Boş telefon `null` gider, `""` değil. */
export function govdeyeCevir(f: KayitFormu): {
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  birthDate: string;
  gradeLevel: Sinif;
  fieldId: string | null;
  davetGonder: boolean;
  nationalId: string | null;
  phoneIsPrimary: boolean;
  /*
    Hedef alanlari YALNIZCA DOLDURULMUSSA gonderiliyor.

    Bos alanlari `null` gondermek de calisirdi ama yeni bir kayitta
    silinecek bir sey yok: `null` "temizle" demek ve burada
    temizlenecek bir deger bulunmuyor. Gondermemek daha dogru bir
    cumle — ve denetim kaydina bes bos alan yazdirmiyor.
  */
  targetExamKind?: string;
  targetScore?: number;
  targetRank?: number;
  targetSchool?: string;
  examDate?: string;
} {
  if (f.sinif === "") throw new Error("Sınıf seçilmeden gövde üretilemez.");
  const tel = f.telefon.trim();
  return {
    email: f.eposta.trim(),
    firstName: f.ad.trim(),
    lastName: f.soyad.trim(),
    phone: tel.length > 0 ? tel : null,
    birthDate: f.dogumTarihi,
    gradeLevel: f.sinif,
    fieldId: f.alan === "" ? null : f.alan,
    davetGonder: f.davetGonder,
    // Boş alan `null` gidiyor, `""` değil: `""` benzersizlik kısıtına
    // takılır ve İKİNCİ T.C.'siz kayıt açılamazdı.
    nationalId: f.tcKimlik.trim() || null,
    phoneIsPrimary: f.telefonSahibi,
    ...hedefGovdesi(f),
  };
}

/**
 * Doldurulmuş hedef alanlarını gövdeye çevirir.
 *
 * ================================================================
 * SAYIYA ÇEVİRMEDEN ÖNCE "DOLU MU" SORULUYOR
 * ================================================================
 * `Number("")` **sıfır** üretiyor. Boş bir hedef puan alanı sunucuya
 * `0` olarak gitseydi kayıt "hedef puanı sıfır" diye açılırdı ve
 * ekran onu girilmiş bir hedef gibi gösterirdi — bu depodaki
 * "ölçülmedi ≠ sıfır" kuralının en ucuz ihlali.
 *
 * Geçersiz sayı da GÖNDERİLMİYOR: `Number("abc")` `NaN` ve `NaN`
 * sunucunun `finite()` kontrolüne takılır, ama kullanıcı neyin yanlış
 * olduğunu anlamaz. Alan `type="number"` olduğu için tarayıcı zaten
 * çoğunu eliyor; kalanı burada susturuluyor.
 */
function hedefGovdesi(f: KayitFormu): {
  targetExamKind?: string;
  targetScore?: number;
  targetRank?: number;
  targetSchool?: string;
  examDate?: string;
} {
  const sayi = (d: string): number | undefined => {
    const k = d.trim();
    if (k.length === 0) return undefined;
    const n = Number(k);
    return Number.isFinite(n) ? n : undefined;
  };
  const metin = (d: string): string | undefined => d.trim() || undefined;

  return {
    ...(metin(f.hedefSinav) !== undefined ? { targetExamKind: f.hedefSinav.trim() } : {}),
    ...(sayi(f.hedefPuan) !== undefined ? { targetScore: sayi(f.hedefPuan) } : {}),
    ...(sayi(f.hedefSiralama) !== undefined ? { targetRank: sayi(f.hedefSiralama) } : {}),
    ...(metin(f.hedefOkul) !== undefined ? { targetSchool: f.hedefOkul.trim() } : {}),
    ...(metin(f.sinavTarihi) !== undefined ? { examDate: f.sinavTarihi.trim() } : {}),
  };
}
